/**
 * Політики кешу запитів (baseline А6; design-system/stability.md, розд. 3): свіжість (`staleTime`)
 * і час життя в кеші (`gcTime`) задаються тут за типом ресурсу, а не числом у кожному `useQuery`.
 * Запит бере політику розгортанням: `useQuery({ queryKey, queryFn, ...queryPolicy.list })`.
 * Літерали `staleTime`/`gcTime` поза цим файлом лінт забороняє (`scripts/ci/check-web-stability.mjs`).
 *
 * Свіжість даних запису тримають події SSE (`events.ts`): `staleTime` — лише страховка на випадок
 * без потоку, а не головне джерело оновлення. Тому список живе 5 хв, а не 30 с (суперечність 1
 * baseline: speed.md UX-34 виправлено). Ліміт кешу UX-34 «≤ 3 списки й 20 карток» не реалізований:
 * `gcTime` — типовий для всіх (Next).
 *
 * Політика без `gcTime` бере типовий з `queryPolicy.default` (клієнт у main.tsx). Значення —
 * лише тут: stability.md, розд. 3, перелічує політики без чисел і посилається на цей файл.
 */

/** Політика: свіжість і, за потреби, власний час життя в кеші. */
export interface QueryPolicy {
  readonly staleTime: number;
  readonly gcTime?: number;
  /** Перечитування, поки запит має глядача: лише для знімка без подій запису. */
  readonly refetchInterval?: number;
}

const MINUTE = 60_000;

export const queryPolicy = {
  /** Типове клієнта (main.tsx): 30 с свіжості, 5 хв у кеші після останнього глядача. */
  default: { staleTime: 30_000, gcTime: 5 * MINUTE },
  /** Метадані платформи (`/meta`): 60 с; зміну схеми приносить подія `schema.changed`. */
  meta: { staleTime: MINUTE },
  /**
   * Списки подань і конфігурація подання людини: 5 хв, свіжість дає живе оновлення (LST-18).
   * Стовпці дошки й «Мої задачі» — `record`: їх перечитує подія запису.
   */
  list: { staleTime: 5 * MINUTE },
  /**
   * Запис і його частини (картка, пов'язане, лінія «Стрічки», підказки поля-зв'язку), стовпці
   * дошки, «Мої задачі»: 30 с і подія SSE.
   */
  record: { staleTime: 30_000 },
  /** Підказка, що живе недовго (перевірка дублів, DUP): 30 с, з кешу — через 60 с. */
  hint: { staleTime: 30_000, gcTime: MINUTE },
  /** Лічильники подань і опитування без подій: 60 с (views.md, розд. 7). */
  counts: { staleTime: MINUTE },
  /** Довідники, профіль людини й компанії: 5 хв, зміну приносить подія або дія людини. */
  reference: { staleTime: 5 * MINUTE },
  /** Свіже на кожен показ (адмінка, кошик, опитування стану): перечитати при монтуванні. */
  fresh: { staleTime: 0 },
  /** Свіже на кожен показ і коротко в кеші (використання поля перед видаленням). */
  freshShort: { staleTime: 0, gcTime: 30_000 },
  /** Одноразове: діалог чи форма беруть свіжі дані на відкриття, після закриття — без кешу. */
  oneShot: { staleTime: 0, gcTime: 0 },
  /**
   * Знімок редактора: не перечитується під курсором і не переживає закриття (`gcTime: 0`) —
   * наступне відкриття бере свіжий стан (макет, воронки, набір значень, звіт аудиту).
   */
  editor: { staleTime: Number.POSITIVE_INFINITY, gcTime: 0 },
  /**
   * Знімок на екрані без подій запису (вікно вхідного дзвінка, CALL-10): права могли змінитись,
   * тож перечитувати кожні 30 с, поки він на екрані, і не тримати після закриття (SEC-046; рев'ю
   * #1085, Security S2 → #1091).
   */
  onScreen: { staleTime: 30_000, gcTime: 0, refetchInterval: 30_000 },
  /** Незмінне за сесію (форми налаштувань, подання платформи й компанії): без перечитування. */
  stable: { staleTime: Number.POSITIVE_INFINITY },
} as const satisfies Record<string, QueryPolicy>;

export type QueryPolicyName = keyof typeof queryPolicy;
